/**
 * Générateur — Automatisme : lier l'expression « une chance sur quatre »
 * (par exemple) et la probabilité 1/4.
 * Attendu ID : 1083 | Niveau : 5ème | Domaine : Probabilités
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Dire et écrire les chances   (carte FUSIONNÉE : phrase → fraction et
 *     fraction → phrase, deux QCM d'une même traduction)
 *   - Chances : toujours vrai ?    (V/F logique, cadence raisonnement)
 *   (situation_qcm_unique RETIRÉ, revue 2026-09-02, décision prof : la
 *   tâche situation → expression relève de l'attendu voisin.)
 *
 * ── Plan de cartes validé (2026-09-01) ─────────────────────────────────────
 * Source : Automatisme n°45 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * APPUIS : « a chances sur b » dès le CM2, relié en 6e au quotient a/b ;
 * équivalences (2/5 = « 4 chances sur 10 ») ; % ↔ « chances sur 100 ».
 * OBSTACLES P11 :
 *   - le passage de « a chances sur b » au QUOTIENT a/b (grille P11) —
 *     piège d'inversion (10/3) servi dans les deux sens de traduction ;
 *   - dans « a chances sur b », b est le nombre TOTAL de cas possibles,
 *     JAMAIS le nombre de cas favorables (fiche n°45 : « 3 boules gagnantes
 *     donc une chance sur 3 » ; erreurs ③ « 4 chances sur 4 » et
 *     ④ « 1 chance sur 4 » pour 4 cartes gagnantes sur 20) — porté par les
 *     lignes du V/F (« il manque le total », « le vingt est le nombre de
 *     cartes gagnantes ») et le piège « favorables sur perdants » du QCM.
 * INVARIANTS :
 *   - unicité par VALEUR recomptée : aucun distracteur n'est jamais
 *     ÉQUIVALENT à la bonne réponse (« 1 chance sur 5 » pour 4/20 est juste,
 *     donc jamais un piège) — filtre numérique, throw si moins de 3 ;
 *   - nombres en toutes lettres dans les phrases (« sur vingt »), fractions
 *     irréductibles au vivier de traduction (pas d'ambiguïté d'équivalence),
 *     dénominateurs ≤ 20 (P3) ;
 *   - aucun calcul exigé, pas de calculatrice.
 */
import {
    randomChoice, shuffle,
    makeQcm, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 1083;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_TRADUIRE = 'Dire et écrire les chances';
const L_VF = 'Chances : toujours vrai ?';

// ─── Le lexique des phrases ────────────────────────────────────────────────

const MOTS_1083 = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix',
    'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf', 'vingt'];

const phraseChances = (a, b) => `${a === 1 ? 'une chance' : `${MOTS_1083[a]} chances`} sur ${MOTS_1083[b]}`;
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const gcd = (x, y) => (y ? gcd(y, x % y) : x);

// Vivier de traduction : fractions IRRÉDUCTIBLES (aucune ambiguïté
// d'équivalence entre une phrase et sa fraction), dénominateurs ≤ 20.
const POOL_FRACTIONS_1083 = [
    [1, 2], [1, 3], [2, 3], [1, 4], [3, 4], [1, 5], [2, 5], [3, 5], [4, 5],
    [1, 6], [5, 6], [3, 8], [5, 8], [7, 8], [1, 10], [3, 10], [7, 10], [9, 10], [1, 20], [3, 20],
];
POOL_FRACTIONS_1083.forEach(([a, b]) => {
    if (a <= 0 || a >= b || gcd(a, b) !== 1) throw new Error('gen_1083 : vivier de fractions invalide');
});

/**
 * Assemble 3 distracteurs : pièges d'abord (inversion, complément,
 * favorables sur perdants), banque ensuite — filtre par VALEUR (jamais un
 * équivalent de la bonne réponse) et par texte (pas de doublon).
 */
const choisirDistracteurs = (candidats, correctTexte, correctValeur, valeurDe) => {
    const retenus = [];
    for (const c of candidats) {
        if (retenus.length >= 3) break;
        if (c === correctTexte || retenus.includes(c)) continue;
        const v = valeurDe(c);
        if (!Number.isFinite(v) || v <= 0 || Math.abs(v - correctValeur) < 1e-9) continue;
        retenus.push(c);
    }
    if (retenus.length < 3) throw new Error('gen_1083 : distracteurs insuffisants');
    return retenus;
};

const valeurFraction = (s) => {
    const m = String(s).match(/^(\d+)\/(\d+)$/);
    return m && +m[2] > 0 ? +m[1] / +m[2] : NaN;
};
const valeurPhrase = (s) => {
    const m = String(s).match(/^(\S+) chances? sur (\S+)$/);
    if (!m) return NaN;
    const a = MOTS_1083.indexOf(m[1] === 'une' ? 'une' : m[1]);
    const b = MOTS_1083.indexOf(m[2]);
    return a > 0 && b > 0 ? a / b : NaN;
};

// ─── Format 1a : de la phrase à la fraction ────────────────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignaturePhraseFraction1083 = null;

const genPhraseFraction = () => {
    let payload = construirePhraseFraction();
    for (let t = 0; t < 8 && payload.texte === derniereSignaturePhraseFraction1083; t++) {
        payload = construirePhraseFraction();
    }
    derniereSignaturePhraseFraction1083 = payload.texte;
    return payload;
};

const construirePhraseFraction = () => {
    const [a, b] = randomChoice(POOL_FRACTIONS_1083);
    const correct = `${a}/${b}`;
    const candidats = [`${b}/${a}`];                              // inversion (P11)
    if (b - a !== a) candidats.push(`${b - a}/${b}`);             // complément
    if (b - a >= 2 && b - a !== b) candidats.push(`${a}/${b - a}`); // favorables sur perdants
    candidats.push(...shuffle(POOL_FRACTIONS_1083.map(([x, y]) => `${x}/${y}`)));
    const distracteurs = choisirDistracteurs(candidats, correct, a / b, valeurFraction);

    const texte = `<p>« ${cap(phraseChances(a, b))} ».</p><p>Quelle fraction écrit cette probabilité ?</p>`;
    const payload = makeQcm([correct, ...distracteurs], correct, texte, opts(L_TRADUIRE));
    payload._meta.params = { a, b, sens: 'phrase_vers_fraction' };
    return payload;
};

// ─── Format 1b : de la fraction à la phrase ────────────────────────────────

// Anti-répétition immédiate (recette standard).
let derniereSignatureFractionPhrase1083 = null;

const genFractionPhrase = () => {
    let payload = construireFractionPhrase();
    for (let t = 0; t < 8 && payload.texte === derniereSignatureFractionPhrase1083; t++) {
        payload = construireFractionPhrase();
    }
    derniereSignatureFractionPhrase1083 = payload.texte;
    return payload;
};

const construireFractionPhrase = () => {
    const [a, b] = randomChoice(POOL_FRACTIONS_1083);
    const correct = phraseChances(a, b);
    // Inversion (P11) — seulement quand elle reste dicible (« sur une » ne
    // se dit pas : pour a = 1, les autres pièges prennent le relais).
    const candidats = a >= 2 ? [phraseChances(b, a)] : [];
    if (b - a !== a) candidats.push(phraseChances(b - a, b));     // complément
    if (b - a >= 2 && b - a !== b) candidats.push(phraseChances(a, b - a)); // favorables sur perdants
    candidats.push(...shuffle(POOL_FRACTIONS_1083.map(([x, y]) => phraseChances(x, y))));
    const distracteurs = choisirDistracteurs(candidats, correct, a / b, valeurPhrase);

    const texte = `<p>La probabilité ${a}/${b}.</p><p>Comment se dit-elle avec des chances ?</p>`;
    const payload = makeQcm(
        [cap(correct), ...distracteurs.map(cap)],
        cap(correct),
        texte,
        opts(L_TRADUIRE),
    );
    payload._meta.params = { a, b, sens: 'fraction_vers_phrase' };
    return payload;
};

// ─── Format 3 : V/F logique (le sens de « a chances sur b ») ───────────────

const POOL_CHANCES = [
    // Pool PURIFIÉ (revue 2026-09-02, décision prof) : uniquement la LIAISON
    // expression ↔ fraction — les lignes interprétant l'évènement (impossible,
    // certain, plus probable) relèvent du 1081, la ligne fréquentiste du 980.
    // 16 lignes (8 V / 8 F) pour la rotation en paquet ; dénominateurs neufs
    // (7, 9, 11, 12, 6/10) hors du vivier de traduction.
    { l: '« Deux chances sur cinq » et « quatre chances sur dix » désignent la même probabilité.', v: true },
    { l: '« Deux chances sur sept » s\'écrit 2/7.', v: true },
    { l: '« Cinq chances sur neuf » s\'écrit 5/9.', v: true },
    { l: '« Quatre chances sur sept » et 4/7 désignent le même nombre.', v: true },
    { l: '« Zéro chance sur cinq » s\'écrit 0/5.', v: true },
    { l: '« Trois chances sur onze » s\'écrit 3/11.', v: true },
    { l: '« Une chance sur quatre » et 1/4 désignent le même nombre.', v: true },
    { l: '« Sept chances sur douze » s\'écrit 7/12.', v: true },
    // L'erreur de la série flash : il manque le nombre TOTAL de boules.
    { l: 'Trois boules gagnantes dans un sac : on a forcément une chance sur trois de gagner.', v: false },
    { l: '« Une chance sur quatre » s\'écrit avec la fraction 4/1.', v: false },
    { l: '« 30 % de chances de gagner » veut dire « trente chances sur dix ».', v: false },
    { l: 'Dans « quatre chances sur vingt », le vingt est le nombre de cartes gagnantes.', v: false },
    { l: '« Deux chances sur trois » s\'écrit 2,3.', v: false },
    { l: '« Six chances sur dix » s\'écrit 10/6.', v: false },
    { l: 'On peut dire « neuf chances sur sept » : le nombre de chances peut dépasser le total.', v: false },
    { l: 'La fraction 3/8 se dit « huit chances sur trois ».', v: false },
];

// FENÊTRE GLISSANTE (revue 2026-09-02, décision prof) : les 8 lignes des
// DEUX dernières versions sont exclues du tirage — aucune ligne ne peut
// revenir à moins de 3 versions d'écart. Contrairement à une rotation en
// paquet (qui figerait le pool en deux blocs disjoints), le tirage libre
// parmi les 8 restantes laisse les combinaisons se recomposer. Mixité :
// 1 à 3 faux par tirage, toujours atteignable (les 8 disponibles portent
// entre 2 et 6 faux).
let derniersServisVf1083 = [];

const genChancesVf = () => {
    let tirage = null;
    for (let essai = 0; essai < 60 && !tirage; essai++) {
        const dispo = POOL_CHANCES.filter((it) => !derniersServisVf1083.includes(it.l));
        const cand = shuffle(dispo).slice(0, 4);
        const nbFaux = cand.filter((it) => !it.v).length;
        if (nbFaux >= 1 && nbFaux <= 3) tirage = cand;
    }
    if (!tirage) throw new Error('gen_1083 : tirage V/F sans mixité après 60 essais');
    derniersServisVf1083 = [...derniersServisVf1083, ...tirage.map((it) => it.l)].slice(-8);

    const payload = makeVraiFaux(
        tirage.map((it) => it.l),
        tirage.map((it) => (it.v ? 0 : 1)),
        '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
        opts(L_VF),
    );
    // Métadonnée DORMANTE (convention gen_1039) : cadence de raisonnement.
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    return payload;
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    phrase_fraction_qcm_unique: genPhraseFraction,
    fraction_phrase_qcm_unique: genFractionPhrase,
    chances_tableau_vf: genChancesVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    {
        // Carte FUSIONNÉE : les deux sens d'une même traduction.
        id: 'traduire',
        label: L_TRADUIRE,
        formatsList: [...buildFormats('phrase_fraction'), ...buildFormats('fraction_phrase')],
    },
    { id: 'chances', label: L_VF, formatsList: buildFormats('chances') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
